import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { filter, take, takeUntil } from 'rxjs';
import { UploadStudyModalComponent } from 'src/app/clinic-studies/upload-study-modal/upload-study-modal.component';
import Constants from 'src/app/constants.json';
import { StudyFile } from 'src/app/models/Study/study-file.model';
import { SaleMediaConversionLog } from 'src/app/models/sales/sale-media-conversion-log.model';
import { SaleHorseSetting } from 'src/app/models/sales/sale-horse-settings.model';
import { AuthService } from 'src/app/services/auth.service';
import { HttpErrorHandlerService } from 'src/app/services/http-error-handler.service';
import { SalesService } from 'src/app/services/sales.service';
import { ViewerService } from 'src/app/services/viewer.service';
import { BaseUnsubscribeComponent } from 'src/app/shared-new/components/base-unsubscribe/base-unsubscribe.component';
import { BackgroundFileService } from 'src/app/shared-new/services/background-file.service';
import { ToastService } from 'src/app/shared-new/services/toast.service';
import { StudiesFacade } from 'src/app/shared-new/store/studies/studies.facade';
import Swal from 'sweetalert2';
import { StudyService } from '../../../../shared-new/services/study.service';
import { EventsEmitter } from '../../../../shared/event-emitter/EventEmitter';
import { MediaGroup } from '../../../../models/media/media-group.model';
import { HorseStudyAcknowledgmentsDto, StudyDicomInfo, StudyDraftSubmissionAcknowledgementsRequestDto, SaleHorseStudyBase } from '../../../../models/Study/study.model';
import { ClinicalStudySumbissionAcknowledgmentsPopupComponent } from '../clinical-study-sumbission-acknowledgments-popup/clinical-study-sumbission-acknowledgments-popup.component';
import { SubmissionValidationPopupComponent } from '../submission-validation-popup/submission-validation-popup.component';
import { SubmissionValidationIssue } from '../../../../models/Study/submission-validation-issue.model';

@Component({
  selector: 'app-upload-study',
  templateUrl: './upload-study.component.html',
  styleUrls: ['./upload-study.component.scss'],
})
export class UploadStudyComponent extends BaseUnsubscribeComponent {
  @Input() saleId: number;
  @Input() horseId: number;
  @Input() studyId: number;
  @Input() saleName: string = '';
  @Input() horseName: string = '';
  @Input() lot: string = '';
  @Input() saleSettings: SaleHorseSetting;
  @Input() autoApproveSubmissions: boolean = false;
  @Output() closedClicked = new EventEmitter();

  allStudyFiles$ = this.studyFacade.studyFilesToUpload$;
  mp4StudyFiles$ = this.studyFacade.mp4StudyFiles$;
  pdfStudyFiles$ = this.studyFacade.pdfStudyFiles$;
  additionalStudyFiles$ = this.studyFacade.additionalStudyFiles$;
  unmatchedStudyFiles$ = this.studyFacade.unmatchedStudyFiles$;


  studyValidationMessages$ =
    this.studyFacade.selectSubmitStudyValidationMessages$;
  activeSaleHorseStudy$ = this.studyFacade.activeSaleHorseStudy$;

  xrayAllStudyFiles$ = this.studyFacade.xrayAllStudyFiles$;
  activeStudyStatus$ = this.studyFacade.activeStudyStatus$;

  mediaGroups$ = this.studyFacade.mediaGroups$;
  mediaGroupsAll$ = this.studyFacade.mediaGroupsAll$;
  mediaCategories$ = this.studyFacade.rawMediaCategories$;
  filteredMediaCategories$ = this.studyFacade.mediaCategories$;
  missingMediaCategories$ = this.studyFacade.missingMediaCategories$;
  xraysMediaGroups: any;

  studyFilesByTypeAndStatusMap$ =
    this.studyFacade.studyFilesByTypeAndStatusMap$;

  statuses$ = this.studyFacade.statuses$;
  activityLogId: number = 0;
  isActivtyEnded: boolean = true;

  scopeDatasource: any[] = [];
  reportDatasource: any[] = [];
  xrayDatasource: any[] = [];

  errorMessages: string = null;
  loggedInMemberId: number = null;
  conversionLogMap: { [studyFileId: number]: SaleMediaConversionLog } = {};
  @Input() public catalogDataSource: StudyDicomInfo[] = [];
  @Input() public lotOrHipText: string = '';
  
  private study: SaleHorseStudyBase; // Store study locally to avoid repeated subscriptions

  constructor(
    private studyFacade: StudiesFacade,
    private router: Router,
    private modalService: NgbModal,
    private viewerService: ViewerService,
    private studyService: StudyService,
    private toaster: ToastService,
    private httpErrorHandler: HttpErrorHandlerService,
    private authService: AuthService,
    private backgroundFileService: BackgroundFileService,
    private salesService: SalesService
  ) {
    super();
    this.authService
      .getUserData()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data) => {
        const user = JSON.parse(data);
        this.loggedInMemberId = user.memberId;
      });

  }

  ngOnInit(): void {
    // Subscribe to activeSaleHorseStudy$ and store the study locally
    // Use filter to skip null initial state and takeUntil to keep subscription alive for updates
    this.activeSaleHorseStudy$
      .pipe(
        filter((study) => !!study),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((study) => {
        this.study = study;
      });

    this.loadConversionLogs();
    this.studyFacade.getXraysMediaGroups(this.loggedInMemberId).pipe(takeUntil(this.unsubscribe$))
      .subscribe((mediaGroups: any) => {
        this.xraysMediaGroups = mediaGroups;
      });
    this.studyFacade.studyFileError$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((error: any) => {
        this.httpErrorHandler.ShowErrorMessage(error);
        this.studyFacade.clearDeleteFileError();
      });
    this.studyFacade.cancelStudyAndDeleteFilesSuccess$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((response) => {
        const component = this.getComponentName();
        const isSaleProfileComponent = component == 'SaleProfileComponent';
        if (response === true && !isSaleProfileComponent) {
          this.router.navigate(['/clinic-studies']);
        }
      });

    this.studyFacade.cancelStudyAndDeleteFilesError$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((response) => {
        this.httpErrorHandler.ShowErrorMessage(response, 'Error');
        this.studyFacade.clearCancelStudyAndDeleteFilesError();
      });

  }

  loadConversionLogs(): void {
    if (!this.saleId) {
      return;
    }
    this.salesService.getSaleMediaConversionLogs(this.saleId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (logs) => {
          this.conversionLogMap = {};
          logs.forEach(log => {
            this.conversionLogMap[log.studyFileId] = log;
          });
        },
        error: () => {
          this.conversionLogMap = {};
        }
      });
  }

  getComponentName() {
    const currentRoute = this.router.routerState.snapshot.root;
    const routeConfig = this.getDeepestRoute(currentRoute);
    return routeConfig.component?.name;
  }

  getDeepestRoute(route: ActivatedRouteSnapshot) {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.routeConfig;
  }

  filterByMember(studyFiles: StudyFile[]) {
    return (
      studyFiles?.filter((a) => a.memberId === this.loggedInMemberId) ?? []
    );
  }

  submitStudyFilesAsAdditional(studyFiles: StudyFile[]): void {
    studyFiles.forEach((studyFile) => {
      this.studyFacade.updateStudyFile(
        studyFile.studyFileId,
        studyFile.mediaCategoryId,
        false,
        true,
        true,
        this.studyId,
        this.saleId,
        this.horseId
      );

    });
  }

  updateStudyFileCategory(model: {
    studyFile: StudyFile,
    mediaCategoryId: number,
    replacing: boolean,
    extra: boolean
  }): void {

    let isAdditional = model.studyFile.isAdditional;
    this.studyFacade.updateStudyFile(
      model.studyFile.studyFileId,
      model.mediaCategoryId,
      model.replacing,
      model.extra,
      isAdditional,
      this.studyId,
      this.saleId,
      this.horseId
    );
    return;


  }

  //assignOrReplaceCategory(model: {
  //  studyFile: StudyFile;
  //  mediaCategoryId: number;
  //}): void {
  //  this.matchedStudyFiles$.pipe(take(1)).subscribe((studyFiles) => {
  //    let isAdditional = model.studyFile.isAdditional;
  //    if (
  //      !studyFiles.find((sf) => sf.mediaCategoryId === model.mediaCategoryId)
  //    ) {
  //      // we don't have a matched studyFile with this assigned category, so let's remove this one from the
  //      // additional study files.
  //      isAdditional = false;
  //    }

  //    this.studyFacade.updateStudyFile(
  //      model.studyFile.studyFileId,
  //      isAdditional,
  //      model.mediaCategoryId
  //    );
  //  });
  //}

  updateStudyFileIsAdditional(model: {
    studyFile: StudyFile;
    isAdditional: boolean;
  }): void {
    this.studyFacade.updateStudyFile(
      model.studyFile.studyFileId,
      model.studyFile.mediaCategoryId,
      false,
      true,
      model.isAdditional,
      this.studyId,
      this.saleId,
      this.horseId
    );
  }

  createStudyFileCheck($event?: any): void {
    //this.backgroundFileService.filesUploading
    //  .pipe(
    //    filter((x) => !x),
    //    takeUntil(this.unsubscribe$)
    //  )
    //  .subscribe((filesUploading) => {
    //    if (!filesUploading) {
    //      this.activeSaleHorseStudy$.pipe(take(1)).subscribe((study) => {
    //        if (study.saleId > 0 && study.horseId) {
    //          this.studyFacade.getSaleHorseStudies(study.saleId, study.horseId);
    //        }
    //      });
    //    }
    //  });
  }

  createStudyFileWithMediaCategoryId(data: {
    file: File;
    mediaCategoryId: number;
  }): void {
    this.studyFacade.createStudyFile(
      this.saleId,
      this.horseId,
      data.file,
      this.studyId,
      false,
      data.mediaCategoryId
    );
  }

  deleteStudyFile(studyFileId: number): void {
    Swal.fire({
      title: Constants.messages.deleteStudyFile.title,
      text: Constants.messages.deleteStudyFile.messageText,
      showCancelButton: true,
      cancelButtonText: Constants.messages.cancelButtonText,
      cancelButtonColor: '#6c757d',
      confirmButtonText: Constants.messages.deleteStudyFile.confirmButtonText,
      confirmButtonColor: '#1b6da6',
    }).then((result) => {
      if (result.value) {
        this.studyFacade.deleteStudyFile(studyFileId);
      }
    });
  }

  cancelStudyAndDeleteFiles(): void {
    Swal.fire({
      title: 'Delete Study & Delete Files',
      html: `
          <h5>Are you sure you want to delete this Study and delete all of its files? This is an irreversible action!?</h5></br>
          <label>
            <input type="checkbox" id="deleteCheckbox" />
            I confirm that I want to delete the study and files
          </label>
        `,
      width: 600,
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
      confirmButtonColor: '#1b6da6',
      willOpen: () => {
        // Disable the delete button initially
        const confirmButton = Swal.getConfirmButton();
        confirmButton.disabled = true;

        // Enable/disable the button based on checkbox state
        const checkbox = document.getElementById('deleteCheckbox');
        checkbox.addEventListener('change', function () {
          confirmButton.disabled = !(this as unknown as HTMLInputElement)
            .checked;
        });
      },
    }).then((result) => {
      if (result.value) {
        this.studyFacade.cancelStudyAndDeleteFiles(
          this.saleId,
          this.horseId,
          0
        );
      }
    });
  }

  cancelStudySubmission(): void {
    Swal.fire({
      title: Constants.messages.cancelSubmission.title,
      html: Constants.messages.cancelSubmission.messageText,
      cancelButtonText: Constants.messages.cancelSubmission.cancelButtonText,
      width: 600,
      showCancelButton: true,
      cancelButtonColor: '#6c757d',
      confirmButtonText: Constants.messages.cancelSubmission.confirmButtonText,
      confirmButtonColor: '#1b6da6',
      willOpen: () => {
        // Disable the delete button initially
        const confirmButton = Swal.getConfirmButton();
        confirmButton.disabled = true;

        // Enable/disable the button based on checkbox state
        const checkbox = document.getElementById('deleteCheckbox');
        checkbox.addEventListener('change', function () {
          confirmButton.disabled = !(this as unknown as HTMLInputElement)
            .checked;
        });
      },
    }).then((result) => {
      if (result.value) {
        /*
        Spinner was behaving unexpectedly, that's why we used service here directly
        */
        let studyId = 0;
        if (this.study.saleHorseStudies && this.study.saleHorseStudies.length > 0) {
          studyId = this.study.saleHorseStudies[0].studyId;
        }
        this.studyFacade.cancelStudySubmission(this.saleId, this.horseId);
        this.closeModal();
      }
    });
  }

  //submitStudyConfirmation(): void {
  //  this.activeSaleHorseStudy$.pipe(take(1)).subscribe((study) => {

  //    if (study.saleHorse && typeof study.saleHorse === "object") {
  //      this.studyService.getHorseStudyConfirmation(this.saleId, this.horseId).pipe(take(1)).subscribe((confirmation: HorseStudyConfirmationDto) => {
  //        const modalRef = this.modalService.open(ClinicalStudySumbissionConfirmationPopupComponent, {
  //          size: 'lg',
  //          centered: true,
  //          keyboard: false,
  //          backdrop: 'static',
  //        });
  //        modalRef.componentInstance.saleId = this.saleId;
  //        modalRef.componentInstance.horseId = this.horseId;
  //        modalRef.componentInstance.studyId = this.studyId;
  //        modalRef.componentInstance.saleHorse = study.saleHorse;
  //        modalRef.componentInstance.study = study;
  //        modalRef.componentInstance.confirmation = confirmation;
  //        modalRef.componentInstance.catalogDataSource = this.catalogDataSource;
  //        modalRef.componentInstance.lotOrHipText = this.lotOrHipText;

  //        modalRef.result.then((result) => {
  //          if (result?.length) {
  //            // we need to know when the files have been uploaded so we can refresh the data on the page.
  //            // this.createStudyFileCheck();
  //          }
  //        });
  //      })
  //    }
  //  });
  //}

  submitStudy(): void {
    this.studyValidationMessages$
      .pipe(take(1))
      .subscribe((response: SubmissionValidationIssue[]) => {
        this.errorMessages = null;

        if (response === null || response?.length === 0) {
          // no validation errors → show confirmation popup
          if (this.study.saleHorse && typeof this.study.saleHorse === 'object') {
            this.studyService.getHorseStudyConfirmation(this.saleId, this.horseId).pipe(take(1)).subscribe((acknowledgments: HorseStudyAcknowledgmentsDto) => {
              const size = acknowledgments.horseStudies.length > 1 ? 'xl' : 'lg';
              const modalRef = this.modalService.open(
                ClinicalStudySumbissionAcknowledgmentsPopupComponent,
                {
                  size: size,
                  keyboard: false,
                  backdrop: 'static',
                }
              );

              modalRef.componentInstance.saleId = this.saleId;
              modalRef.componentInstance.horseId = this.horseId;
              modalRef.componentInstance.studyId = this.studyId;
              modalRef.componentInstance.saleHorse = this.study.saleHorse;
              modalRef.componentInstance.study = this.study;
              modalRef.componentInstance.acknowledgments = acknowledgments;
              modalRef.componentInstance.catalogDataSource = this.catalogDataSource;
              modalRef.componentInstance.lotOrHipText = this.lotOrHipText;

              modalRef.result.then((result) => {
                if (result.confirmation === 'confirm') {
                  // after confirmation, update files
                  this.allStudyFiles$.pipe(take(1)).subscribe((studyFiles) => {
                    if (this.autoApproveSubmissions) {
                      this.updateStudyFilesAutoApprove(studyFiles, result.acknowledgementData);
                    } else {
                      this.updateStudyFileStatuses(studyFiles, result.acknowledgementData);
                    }
                  });
                }
              });
            });
          }
        } else {
          // Validation failed. Every applicable issue is reported at once, so a
          // study that is short of its X-Ray protocol AND missing a scope video
          // shows both rather than one per submit attempt.
          this.errorMessages = response
            .map((issue, i) => `${i + 1}. ${issue.title}`)
            .join('\n');

          const modalRef = this.modalService.open(
            SubmissionValidationPopupComponent,
            {
              size: 'lg',
              centered: true,
              scrollable: true,
            }
          );

          modalRef.componentInstance.issues = response;
          modalRef.componentInstance.lotOrHipText = this.lotOrHipText;
          modalRef.componentInstance.lot = this.lot;
          modalRef.componentInstance.horseName = this.horseName;

          // The modal is dismissable by backdrop and Esc, which reject the
          // promise. Nothing follows a failed validation, so swallow it rather
          // than letting it surface as an unhandled rejection.
          modalRef.result.catch(() => undefined);
        }
      });
  }


  updateStudyFileStatuses(studyFiles: StudyFile[], acknowledgementData: StudyDraftSubmissionAcknowledgementsRequestDto): void {
    this.studyFacade.updateStudyFileStatuses(
      studyFiles.filter((a) => a.studyStatusId < 2).map((sf) => sf.studyFileId),
      2, null, null, acknowledgementData
    );
  }

  updateStudyFilesAutoApprove(studyFiles: StudyFile[], acknowledgementData: StudyDraftSubmissionAcknowledgementsRequestDto): void {
    this.studyFacade.updateStudyFilesAutoApprove(
      studyFiles.filter((a) => a.studyStatusId < 2).map((sf) => sf.studyFileId),
      4, null, null, acknowledgementData
    );
  }

  updateStudyFileStatus(data: {
    studyFileId: number;
    statusId: number;
    reason: string;
  }): void {
    this.studyFacade.updateStudyFileStatuses(
      [data.studyFileId],
      data.statusId,
      data.reason
    );
  }

  closeModal(): void {
    this.closedClicked.emit();
  }
  hideWarningAlert() {
    this.errorMessages = null;
  }
  resetStatuses(): void {
    this.allStudyFiles$.pipe(take(1)).subscribe((studyFiles) => {
      this.studyFacade.updateStudyStatus(
        this.studyId,
        0,
        studyFiles.map((sf) => sf.studyFileId)
      );
    });
  }

  openVideoModal(studyFileId: number): void {
    if (!this.study) return;
    this.mp4StudyFiles$.pipe(take(1)).subscribe((mp4StudyFiles) => {
      const video = mp4StudyFiles.find(
        (file) => file.studyFileId === studyFileId
      );
      this.viewerService.playVideo(this.study, video, null, false, null, false);
    });
  }

  openUploadModal(): void {
    if (!this.study) return;
    if (this.study.saleHorse && typeof this.study.saleHorse === "object") {
      const modalRef = this.modalService.open(UploadStudyModalComponent, {
        size: 'lg',
        centered: true,
        keyboard: false,
        backdrop: 'static',
      });
      modalRef.componentInstance.saleId = this.saleId;
      modalRef.componentInstance.horseId = this.horseId;
      modalRef.componentInstance.studyId = this.studyId;
      modalRef.componentInstance.saleHorse = this.study.saleHorse;

      modalRef.result.then((result) => {
        if (result?.length) {
          // we need to know when the files have been uploaded so we can refresh the data on the page.
          // this.createStudyFileCheck();
        }

        if (this.study.saleId > 0 && this.study.horseId) {
          this.studyFacade.getSaleHorseStudies(this.study.saleId, this.study.horseId, false);
        }
      });
    }
  }
}
